import { getPrimaryColor, Styles } from '@orcar/common-d2c';
import styled from 'styled-components';
import { app } from '@/constants/common';
import { Accordion } from './guideUi';

const BLUE = getPrimaryColor(app);
const RED = Styles.COLOR.RED;

const stroke = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

const IconAge = (
  <svg viewBox='0 0 48 48' {...stroke}>
    <circle cx='24' cy='24' r='16' />
    <path d='M24 15v9l6 4' />
  </svg>
);
const IconExp = (
  <svg viewBox='0 0 48 48' {...stroke}>
    <circle cx='24' cy='24' r='16' />
    <circle cx='24' cy='24' r='4' />
    <path d='M24 8v8M24 32v8M8 24h8M32 24h8' />
  </svg>
);
const IconLicense = (
  <svg viewBox='0 0 48 48' {...stroke}>
    <rect x='7' y='12' width='34' height='24' rx='3' />
    <circle cx='17' cy='23' r='4' />
    <path d='M27 20h9M27 26h9M13 31h22' />
  </svg>
);

/**
 * 대여자격 안내 — 네이티브 인포그래픽(일러스트 + 접이식).
 * 핵심 조건은 카드로, 상세 규정/유의사항은 기본 접힘(내용은 DOM 유지 → SEO).
 */
const QualificationGuide = () => {
  return (
    <Wrap>
      <Header>
        <HeaderArt aria-hidden>
          <svg viewBox='0 0 48 48' fill='none'>
            <rect
              x='7'
              y='12'
              width='34'
              height='24'
              rx='3'
              fill='rgba(255,255,255,0.16)'
              stroke='#fff'
              strokeWidth='2'
            />
            <circle cx='17' cy='23' r='4' stroke='#fff' strokeWidth='2' />
            <path
              d='M27 20h9M27 26h9M13 31h22'
              stroke='#fff'
              strokeWidth='2'
              strokeLinecap='round'
            />
          </svg>
        </HeaderArt>
        <HeaderTitle>대여자격 안내</HeaderTitle>
        <HeaderSub>대여 전, 자격 조건과 준비물을 확인하세요!</HeaderSub>
      </Header>

      {/* 1. 기본 대여 조건 */}
      <Section>
        <SecHead>
          <Num>1</Num> 기본 대여 조건
        </SecHead>
        <ThreeCol>
          <Stat>
            <StatIc>{IconAge}</StatIc>
            <StatBig>만 26세 이상</StatBig>
            <StatDesc>운전자 나이 기준</StatDesc>
          </Stat>
          <Stat>
            <StatIc>{IconExp}</StatIc>
            <StatBig>운전경력 2년 이상</StatBig>
            <StatDesc>장롱면허는 제외</StatDesc>
          </Stat>
          <Stat>
            <StatIc>{IconLicense}</StatIc>
            <StatBig>운전면허증 필수</StatBig>
            <StatDesc>실물 지참 필요</StatDesc>
          </Stat>
        </ThreeCol>
        <WarnBanner>
          ⚠️ 슈퍼카 · 쏠라티는 <b>만 30세 이상, 운전경력 3년 이상</b>
        </WarnBanner>
      </Section>

      {/* 2. 대상별 준비물 */}
      <Section>
        <SecHead>
          <Num>2</Num> 대상별 준비물
        </SecHead>
        <ThreeCol>
          <Card>
            <CardHead>내국인</CardHead>
            <List>
              <li>국내 운전면허증</li>
            </List>
          </Card>
          <Card>
            <CardHead>외국인</CardHead>
            <List>
              <li>한국 면허증 소지 시 대여 가능</li>
              <li>국제면허증(IDP) + 개인여권</li>
              <li>국제면허만 있으면 제2운전자만 가능</li>
            </List>
          </Card>
          <Card>
            <CardHead>주한미군</CardHead>
            <List>
              <li>개인여권 + 오렌지카드</li>
            </List>
          </Card>
        </ThreeCol>
      </Section>

      {/* 3. 외국인·국제면허 상세 (접힘) */}
      <Accordion num={3} title='외국인 · 국제면허 상세 규정'>
        <List>
          <li>
            국제면허증 소지 외국인(한국 면허 미소지 재외국민 포함)은{' '}
            <b>제2운전자로만 등록</b> 가능하며, 제1운전자는 반드시 한국 면허증
            소지자여야 합니다.
          </li>
          <li>
            국제운전면허증은 <b>제네바·비엔나·SOFA 가입국 발행 IDP</b>와
            개인여권 소지자에 한합니다.
          </li>
          <li>
            영문 철자가 여권과 다르거나 풀네임 확인이 어려우면 대여 불가합니다.
          </li>
        </List>
      </Accordion>

      {/* 4. 대여 불가 및 유의사항 (접힘) */}
      <Accordion num={4} title='대여 불가 및 유의사항' red>
        <Chips>
          <Chip>현역군인(장교 제외) 대여 불가</Chip>
          <Chip>낚시용품 소지 시 대여 불가</Chip>
          <Chip>반려동물 동반은 '펫과 함께' 경로 이용</Chip>
          <Chip>면허 취소 후 재취득 시 운전경력증명서 지참</Chip>
        </Chips>
      </Accordion>
    </Wrap>
  );
};

export default QualificationGuide;

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Header = styled.div`
  border-radius: 16px;
  padding: 28px 24px;
  color: #fff;
  text-align: center;
  background: linear-gradient(135deg, ${BLUE}, ${getPrimaryColor(app)}cc);
`;

const HeaderArt = styled.div`
  width: 60px;
  margin: 0 auto 10px;

  svg {
    width: 100%;
    height: auto;
    display: block;
  }
`;

const HeaderTitle = styled.h2`
  ${Styles.FONT.H2};
  margin: 0;
  color: #fff;
`;

const HeaderSub = styled.p`
  ${Styles.FONT.B1};
  margin: 8px 0 0;
  color: #fff;
  opacity: 0.95;
`;

const Section = styled.section`
  border: 1px solid ${Styles.COLOR.GREY_05};
  border-radius: 16px;
  padding: 22px;
`;

const SecHead = styled.h3`
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0 0 18px;
  ${Styles.FONT.H3};
  color: ${Styles.COLOR.GREY_01};
`;

const Num = styled.span<{ red?: boolean }>`
  display: grid;
  place-items: center;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  ${Styles.FONT.B1};
  font-weight: 800;
  color: #fff;
  background-color: ${({ red }) => (red ? RED : BLUE)};
`;

const ThreeCol = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 14px;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const Stat = styled.div`
  border: 1px solid ${BLUE}33;
  border-radius: 14px;
  padding: 22px 18px;
  text-align: center;
  background-color: ${BLUE}0a;
`;

const StatIc = styled.div`
  width: 52px;
  height: 52px;
  margin: 0 auto 12px;
  display: grid;
  place-items: center;
  border-radius: 14px;
  color: ${BLUE};
  background-color: ${BLUE}14;

  svg {
    width: 30px;
    height: 30px;
  }
`;

const StatBig = styled.div`
  ${Styles.FONT.H4};
  font-weight: 800;
  color: ${Styles.COLOR.GREY_01};
`;

const StatDesc = styled.div`
  ${Styles.FONT.B2};
  color: ${Styles.COLOR.GREY_03};
  margin-top: 6px;
`;

const WarnBanner = styled.div`
  margin-top: 14px;
  padding: 12px 16px;
  border-radius: 10px;
  background-color: ${RED}12;
  border: 1px solid ${RED}40;
  color: ${RED};
  ${Styles.FONT.B1};
  text-align: center;
`;

const Card = styled.div`
  border: 1px solid ${Styles.COLOR.GREY_05};
  border-radius: 14px;
  padding: 20px;
  background-color: ${Styles.COLOR.GREY_BG};
`;

const CardHead = styled.div`
  display: inline-block;
  ${Styles.FONT.B1};
  font-weight: 800;
  color: #fff;
  background-color: ${BLUE};
  padding: 6px 16px;
  border-radius: 999px;
  margin-bottom: 14px;
`;

const List = styled.ul`
  margin: 0;
  padding-left: 18px;
  display: grid;
  gap: 8px;

  li {
    ${Styles.FONT.B1};
    color: ${Styles.COLOR.GREY_01};
  }
`;

const Chips = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const Chip = styled.div`
  ${Styles.FONT.B2};
  color: ${Styles.COLOR.GREY_01};
  background-color: ${RED}0a;
  border: 1px solid ${RED}26;
  border-radius: 10px;
  padding: 12px 14px;
`;
